// @givmo/app-admin — the company-wallet DEPOSIT charge dialog (EP-WEB6). ★ MONEY-MOVING ★
//
// Restores the wallet top-up the retired Flutter portal had (`deposit_wallet_pop_up.dart`) + parity
// with the shipped iOS `CompanyDepositScreen`. Depositing CHARGES A REAL COMPANY CARD via
// `POST /admin/company/wallet/deposit` (the same live route the shipped clients use) and credits the
// company wallet — so this component is where every money-safety invariant is enforced, mirroring the
// dashboard `ApproveMatchDialog`:
//
//   1. IDEMPOTENCY — a stable `Idempotency-Key` is minted ONCE per (amount, card, cover-fees choice)
//      attempt-series and REUSED for any retry of the SAME deposit, with the exact request it went out
//      with. The backend `claim_idempotency` de-dupes only across same-key retries; an unkeyed retry
//      double-charges — so the key is load-bearing, never regenerated on retry. The series is held
//      BEYOND the dialog for the browser session (`deposit-series.ts`, CAM-R51 widened, D-EP-53): per
//      signed-in person (their uid) and company, per signature, in module memory written through to
//      `sessionStorage`, so a
//      close/reopen, a Settings tab switch, leaving the console's Wallet or a reload keeps it, and
//      re-entering the same deposit resends the same body under the same key (the backend replays,
//      never re-charges). A reopen starts from the series in doubt — its amount, card and choice — so
//      pressing Deposit again replays it (CAM-R51).
//   2. NO BLIND RETRY of a MAYBE-CHARGED error — a 408/429/5xx/transport failure is classified
//      RETRYABLE (`isRetryableChargeError`): the charge MAY have gone through, so we surface an explicit
//      "check your balance / try again" state and re-POST under the SAME key only on the user's say-so.
//      A fatal 4xx (decline / validation / 403) rolls back cleanly with no retry. DISMISSAL of the
//      maybe-charged state is itself gated: Esc / overlay-click / Cancel do not close silently — the
//      user must explicitly acknowledge that the charge may have gone through.
//   3. SINGLE-FLIGHT — the Deposit button is disabled + spins while a charge is in flight; a
//      double-click cannot fire a second POST (an in-flight ref AND the disabled button) and cannot
//      mint a second key.
//   4. CONFIRM-BEFORE-CHARGE — the dialog restates the exact amount + funding card in a summary line
//      AND on the primary button ("Deposit $50") before any charge. Once a charge is attempted the
//      amount + card + cover-fees fields LOCK (no divergent series can start that might double-charge
//      a maybe-charged 408).
//   5. CARD REQUIRED — the deposit needs a `card_id`; with NO card on file the charge control is inert
//      and the dialog prompts to add a card first. We never POST a deposit without a card.
//   6. BALANCE/BOUNDS PRE-CHECK — a deposit only ever INCREASES the balance, so there is no spend
//      budget to gate; the applicable pre-charge guard is the amount envelope (whole dollars, min $1 /
//      max $50,000, matching the shipped clients — a client UX bound: the backend enforces only
//      net > 0). An out-of-bounds/empty amount disables Deposit and shows an inline error — we never
//      POST an unvalidated amount. The backend's own decline / validation errors surface as fatal
//      (chargeless) messages.
//
// "Cover processing fees" (CAM-BE11, D-CAM-37): an Owner may cover the card processing fee on top, so
// the wallet receives exactly the amount entered. Off by default on every open, like the personal iOS
// Add Funds row, whose wording its fee line uses. The choice is part of the charge series (a different
// gross is a different charge).
//
// THE PRICE IS THE SERVER'S (CAM-BE11 review F1). Every figure the dialog states comes from
// `GET /admin/company/wallet/deposit/quote` for the current amount + card, which runs the charge's own
// resolution (the stored card's brand row, the company's own setting). Deposit is held while that
// quote loads or has failed; a failed quote leaves the choice unavailable. The POST carries the gross
// the dialog stated (`expected_amount_charged`), and a backend that would charge any other gross
// refuses with `DEPOSIT_QUOTE_CHANGED` before money moves: the dialog then re-reads the quote, says
// nothing was charged and asks again under a new key. A company whose own setting covers every deposit
// (the quote's `company_covers_fees`) shows the row ticked and locked.
//
// A maybe-charged attempt's "Try again" resends EXACTLY the request it made (review F2), frozen when
// the charge started: never a body rebuilt from a quote that may have moved since.
//
// THE DONOR DISCLOSURE (CAM-X4 web, the company deposit). Adding funds is an irrevocable donation to
// Givmo Charitable Fund with the company as its donor, so the dialog shows the approved company body
// (`deposit-disclosure.ts`: `ADD_FUNDS` for a company payer, and only a row resolved for that payer)
// under the "Terms of your donation" divider (D-CAM-57), directly above Cancel and Deposit, in the
// portal's terms panel (D-CAM-44). Deposit waits for it; a failed resolve says so and offers a retry.
// The request freezes the body it was stated under with its other figures, and echoes it
// (`shown_disclaimer_*`), so a retry — or a maybe-charged deposit re-entered after a close — resends
// the same echo and shows the same text. The backend's gate refuses a stale echo with `409
// DISCLOSURE_STALE` before any charge; the dialog then drops the key, re-reads the body and asks again.
//
// The dialog is DRIVEN by the page: the page supplies the vaulted cards, the fee inputs and whose
// deposits these are (`seriesScope`: the signed-in uid from its auth context, the company from its
// admin or wallet read), and owns the query invalidation + toast
// side effects. Two page callbacks: `onDeposited` (confirmed success) and `onCloseAfterAttempt` (the
// dialog closed after ANY charge attempt — success or not — so the page refreshes the wallet balance
// and the "check your company balance" hint shows fresh truth).
//
// THE DUPLICATE CONFIRM (EP-BE15, D-EP-53). A company deposit is an irrevocable donation, and the
// held series covers only this tab, so every new deposit also asks the backend to check for a repeat
// (`duplicate_check: "prompt"`, frozen in the body). While the backend's switch is on, a deposit
// under a new key that repeats the company's deposit of the same amount on the same card from minutes
// ago is refused 409 `DEPOSIT_DUPLICATE_RECENT` before any money moves; the dialog then asks "3 minutes
// ago, $X was added with <card>. Add it again?" — company-wide, so it never says who: the earlier
// deposit may be a teammate's (cut 19's review F2). Its confirm resends the same request with
// `confirm_duplicate: true` under a FRESH key (a deliberate new deposit: its own series), and its
// cancel closes with nothing charged. Installed apps never send the field and are never asked.
//
// ONE DIALOG, TWO HOSTS (D-EP-49, EP-MNT5): the employer portal's Settings → Payment Methods tab and
// the company console's Wallet (`apps/company/src/features/wallet/wallet-page.tsx`) render this same
// component; the console's trimmed port retired with the move. Each host keeps its own gate for who
// may deposit.

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  sdk,
  firstErrorCode,
  firstErrorContext,
  isGivmoError,
  supportReference,
  GivmoError,
} from '@givmo/api-client';
import {
  Button,
  Dialog,
  Icon,
  Select,
  TextField,
  testIdProp,
  type SelectOption,
} from '@givmo/design-system';
import { AlertTriangle } from 'lucide-react';
import { disclosureEcho, isDisclosureStaleError } from '@givmo/disclosure';
import { isRetryableChargeError, maybeChargedLine } from '@givmo/payments';
import {
  ChargeDisclosureTerms,
  DisclosureUnavailable,
  useChargeDisclosure,
} from '../../components/charge-disclosure';
import { coverFeesRowCopy, depositQuoteFigures } from '../../components/cover-fees-copy';
import { CoverFeesRow } from '../../components/cover-fees-row';
import { useRevealOnShow } from '../../components/reveal-on-show';
import { cardLabel, type PaymentCard } from './payment-methods-decode';
import { resolveDepositDisclosure } from './deposit-disclosure';
import { depositQuoteQueryKey, fetchDepositQuote } from './deposit-quote-transport';
import {
  adoptDepositSeries,
  depositSeriesVersion,
  memoryDepositSeries,
  sessionDepositSeries,
  subscribeDepositSeries,
  type CompanyDepositBody,
  type DepositRequest,
  type DepositSeriesEntry,
  type DepositSeriesScope,
} from './deposit-series';
import {
  DEFAULT_DEPOSIT_DOLLARS,
  DEPOSIT_PRESETS,
  depositAmountErrorMessage,
  depositDollars,
  depositEntryFromWire,
  formatChargeFigure,
  formatDollars,
  formatFeeFigure,
  toDepositQuote,
  toDepositWireAmount,
  validateDepositAmount,
} from './wallet-decode';

const PREFIX = 'settings-deposit';

const NO_CARD_BODY =
  'You need a saved company card to add funds. Add one under “Cards on file” below, then come back ' +
  'to deposit.';

const RETRYABLE_HINT =
  'We could not confirm whether the charge went through. Do not assume it failed — check your company ' +
  'balance, or try again. A retry is de-duplicated and will not double-charge.';

const QUOTE_LOADING = 'Checking the processing fee for this card…';

const QUOTE_FAILED =
  'We could not check the processing fee for this card, so this deposit cannot be made right now. ' +
  'Close and try again. Nothing will be charged.';

const QUOTE_CHANGED =
  'The amount this deposit would charge has changed, so nothing was charged. Check the new total, ' +
  'then deposit again.';

// Exactly where a re-entry is safe (D-EP-53, "say exactly where it is safe"): the series is held for
// this tab (`deposit-series.ts`: `sessionStorage` is per tab), so a deposit from another tab or
// another device is a new charge.
const CLOSE_ACK_BODY =
  'The charge may have gone through even though we could not confirm it. After closing, check your ' +
  'company balance before depositing again — re-entering the same amount, card and processing-fee ' +
  'choice in this tab is safe (it will not double-charge), but a different amount, or a deposit ' +
  'from another tab or device, would be a new charge.';

/** The duplicate-deposit refusal (EP-BE15): nothing was charged, and the person is asked. */
const DUPLICATE_RECENT = 'DEPOSIT_DUPLICATE_RECENT';

/** When the earlier deposit was made, as the question's opening words. */
function agoWords(minutesAgo: number | null): string {
  if (minutesAgo === null) return 'A few minutes ago';
  if (minutesAgo < 1) return 'Less than a minute ago';
  return minutesAgo === 1 ? '1 minute ago' : `${minutesAgo} minutes ago`;
}

/**
 * EP-BE15's question, about the earlier deposit the backend named (cut 19's review F2): the time
 * leads, so the card's last four never run into the minutes, and it says what was added, never who
 * — the check is company-wide, so the earlier deposit may be another admin's.
 */
export function duplicateDepositQuestion(args: {
  /** The earlier deposit's amount, as the wire states it (e.g. "50.00"). */
  amount: string;
  /** The card it was made with, as the dialog labels it, or `null` when the card is not on file. */
  card: string | null;
  /** Whole minutes since it was made, or `null` when the backend did not say. */
  minutesAgo: number | null;
}): string {
  return (
    `${agoWords(args.minutesAgo)}, ${formatChargeFigure(args.amount)} was added with ` +
    `${args.card ?? 'this card'}. Add it again?`
  );
}

/** What the duplicate refusal says about the earlier deposit, read tolerantly off its context. */
interface EarlierDeposit {
  readonly amount: string;
  readonly cardId: string;
  readonly minutesAgo: number | null;
}

function earlierDepositOf(err: GivmoError, request: DepositRequest): EarlierDeposit {
  const context = firstErrorContext(err) ?? {};
  const { amount, card_id: cardId, minutes_ago: minutesAgo } = context;
  return {
    // A repeat is the same amount on the same card by definition, so the request's own figures
    // stand in for a field the context lacks.
    amount: typeof amount === 'string' && /^\d+(?:\.\d+)?$/.test(amount) ? amount : request.amount,
    cardId: typeof cardId === 'string' && cardId.length > 0 ? cardId : request.cardId,
    minutesAgo:
      typeof minutesAgo === 'number' && Number.isInteger(minutesAgo) && minutesAgo >= 0
        ? minutesAgo
        : null,
  };
}

// No "nothing has been charged" here: the resolve re-runs on a reopen, which can follow a deposit
// that may have gone through (the CAM-R59 class of copy).
const DISCLOSURE_UNAVAILABLE =
  'We couldn’t load the terms for this deposit, so it can’t be made yet.';

const DISCLOSURE_STALE =
  'The terms for this deposit were just updated. Read the updated terms below, then deposit ' +
  'again. Nothing was charged.';

/** The charge state machine. `retryable-error` REUSES the series: its key AND its request. */
type Phase =
  | { status: 'idle' }
  | { status: 'charging'; entry: DepositSeriesEntry }
  | {
      status: 'retryable-error';
      message: string;
      reference: string | null;
      entry: DepositSeriesEntry;
    }
  | { status: 'fatal-error'; message: string; reference: string | null }
  | { status: 'quote-changed' }
  | { status: 'disclosure-stale' }
  | {
      // EP-BE15: refused as a repeat, nothing charged; the person is asked. The request is the one
      // the backend held back, resent with the confirm under a fresh key.
      status: 'duplicate-recent';
      request: DepositRequest;
      earlier: EarlierDeposit;
    };

export interface DepositDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The company's vaulted cards. Empty → the inert "add a card" state (no charge control). */
  cards: PaymentCard[];
  /** Fired ONCE after the deposit charges successfully (the page invalidates + toasts + closes). */
  onDeposited: () => void;
  /**
   * Fired when the dialog closes after ANY charge attempt in this open-session (fatal error, an
   * acknowledged maybe-charged close, or a post-error cancel) — success closes go through
   * `onDeposited` instead. The page refreshes the wallet query so the balance the user checks next
   * is fresh truth, not a stale cache.
   */
  onCloseAfterAttempt: () => void;
  /**
   * Whose deposits these are — the signed-in person's uid (the host's auth context) and their
   * company when known (its admin read, else its wallet read): `depositSeriesScope`. A deposit's
   * series is held under the uid beyond this dialog, so a re-entry after the page unmounts or reloads
   * replays it (CAM-R51) even when a read has failed (cut 19's review F3). `null` only while the host
   * cannot name the uid: the series is then held in this dialog's memory, and handed over once it can.
   */
  seriesScope: DepositSeriesScope | null;
}

/** The card a reopened dialog starts on: the held request's while it is still on file (CAM-R51). */
function cardForReopen(
  held: DepositRequest | null,
  cards: readonly PaymentCard[],
  defaultCardId: string | null,
): string | null {
  // A card no longer on file cannot be replayed: the reopen falls back to the default card.
  return held !== null && cards.some((c) => c.id === held.cardId) ? held.cardId : defaultCardId;
}

export function DepositDialog({
  open,
  onOpenChange,
  cards,
  onDeposited,
  onCloseAfterAttempt,
  seriesScope,
}: DepositDialogProps) {
  // The charge series, held BEYOND this dialog (`deposit-series.ts`): every unresolved deposit's key
  // and the exact request it went out with, per signature (amount, card, cover-fees choice), for this
  // company and admin. Re-entering a held deposit resends that request under that key, so the backend
  // replays it even if the quote has moved since, and the dialog states THAT request while the entry
  // matches it. Until the host names the uid, the series lives in this dialog's memory (a
  // `useState` initializer, stable across re-renders) and is handed over once it can.
  const [mountSeries] = useState(memoryDepositSeries);
  const scopeUid = seriesScope?.uid ?? null;
  const scopeCompanyId = seriesScope?.companyId ?? null;
  const series = useMemo(
    () =>
      scopeUid !== null
        ? sessionDepositSeries({ uid: scopeUid, companyId: scopeCompanyId })
        : mountSeries,
    [scopeUid, scopeCompanyId, mountSeries],
  );
  // A layout effect, so the hand-over lands before the next input event can read the series.
  useLayoutEffect(() => {
    if (series !== mountSeries) adoptDepositSeries(mountSeries, series);
  }, [series, mountSeries]);
  // Re-render on every change of a held series — including a charge an unmounted copy of this dialog
  // started, whose answer lands after a navigation.
  useSyncExternalStore(subscribeDepositSeries, depositSeriesVersion, depositSeriesVersion);
  const queryClient = useQueryClient();

  const defaultCardId = useMemo(() => {
    const preferred = cards.find((c) => c.isDefault) ?? cards[0];
    return preferred?.id ?? null;
  }, [cards]);

  const [amount, setAmount] = useState(String(DEFAULT_DEPOSIT_DOLLARS));
  const [cardId, setCardId] = useState<string | null>(defaultCardId);
  // CAM-BE11: the per-deposit "Cover processing fees" choice — off by default on every open.
  const [coverFees, setCoverFees] = useState(false);
  const [phase, setPhase] = useState<Phase>({ status: 'idle' });
  // The maybe-charged close acknowledgement step (invariant 2): armed when the user tries to dismiss
  // a `retryable-error` state; only its explicit confirm button closes.
  const [closeAck, setCloseAck] = useState(false);
  // It replaces the footer, so on a phone it opens below the fold: it is brought into view (P5).
  const closeAckRef = useRevealOnShow<HTMLDivElement>(
    phase.status === 'retryable-error' && closeAck,
  );

  // Synchronous double-submit guard: flips true the instant a charge starts, back to false when it
  // settles, so a second click during the same tick is a no-op even before `phase` re-renders.
  const inFlight = useRef(false);
  // Whether ANY charge attempt fired in this open-session — drives `onCloseAfterAttempt` on close.
  const attempted = useRef(false);
  // The held request this open-session started from (CAM-R51), so a card list that arrives after the
  // open still lands on its card.
  const reopenedFrom = useRef<DepositRequest | null>(null);

  // Reset ONLY on a genuine CLOSED→OPEN transition: re-seed amount + card and clear the phase. Gated
  // on a real open transition via `prevOpen` (NOT on `defaultCardId`), so a mid-charge cards refetch
  // that changes the default id never releases the flight lock → which a later 408 "Try again" would
  // otherwise turn into a FRESH key → the backend can't de-dupe → a double charge. (Same Fix-2
  // invariant as the ApproveMatchDialog.)
  //
  // ★ The series is DELIBERATELY NOT cleared here: it keys by signature, so a held deposit stays
  // alive across close/reopen, and re-entering the SAME amount + card + choice reuses the SAME key and
  // request (the backend replays — no double charge), while anything different mints fresh by
  // construction. And the reopen STARTS from the series that went out most recently (CAM-R51): its
  // amount, card and choice, so pressing Deposit again replays it rather than charging a $20,
  // default-card, uncovered deposit beside it. Only a definite outcome clears a series.
  const prevOpen = useRef(false);
  useEffect(() => {
    if (open && !prevOpen.current) {
      const held = series.latest()?.request ?? null;
      reopenedFrom.current = held;
      setAmount(
        (held !== null ? depositEntryFromWire(held.amount) : null) ??
          String(DEFAULT_DEPOSIT_DOLLARS),
      );
      setCardId(cardForReopen(held, cards, defaultCardId));
      setCoverFees(held?.coverFees ?? false);
      setPhase({ status: 'idle' });
      setCloseAck(false);
      attempted.current = false;
      inFlight.current = false;
    }
    prevOpen.current = open;
    // `series`, `cards` and `defaultCardId` are intentionally NOT deps — a reset must fire only on an
    // open-transition, not when a held series, the card list or its default changes. They are read
    // only inside the open-transition branch (correct on the flip).
  }, [open]);

  // Auto-select a card once the card list becomes available (e.g. the cards query resolves AFTER
  // open, so the open-transition reset ran with no card to pick): the held request's card when the
  // dialog reopened from one, else the default. KEY-SAFE: only `setCardId`, never touches the series
  // or the flight lock. Guarded to fire only when nothing is selected AND no charge is in flight, so
  // it never overrides a user selection or an in-flight charge.
  useEffect(() => {
    if (open && cardId === null && !inFlight.current) {
      const next = cardForReopen(reopenedFrom.current, cards, defaultCardId);
      if (next !== null) setCardId(next);
    }
  }, [open, cardId, cards, defaultCardId]);

  const hasCard = cards.length > 0 && cardId !== null;
  const busy = phase.status === 'charging';
  // Once a charge has been attempted (charging OR maybe-charged), the amount + card fields LOCK: a new
  // (amount, card) series while a prior deposit may have gone through is exactly the double-charge
  // vector. They unlock only in `idle`/`fatal-error` (definite no-charge → safe to re-enter).
  const locked = phase.status === 'charging' || phase.status === 'retryable-error';

  const validation = validateDepositAmount(amount);
  const dollars = depositDollars(amount);
  const wireAmount = toDepositWireAmount(amount);

  // CAM-BE11 review F1: the server's price for THIS amount + card. A different amount or card is a
  // different key, so a figure is never stated for a deposit it was not quoted for; `staleTime: 0`
  // because a price must be read fresh each time it is stated (the charge's guard backs it anyway).
  const quoteQuery = useQuery({
    queryKey: depositQuoteQueryKey(wireAmount, cardId),
    queryFn: () => fetchDepositQuote({ amount: wireAmount ?? '', cardId: cardId ?? '' }),
    enabled: open && hasCard && wireAmount !== null,
    select: toDepositQuote,
    staleTime: 0,
  });
  const quoteStatus: 'none' | 'loading' | 'failed' | 'ready' =
    !hasCard || wireAmount === null
      ? 'none'
      : quoteQuery.isPending
        ? 'loading'
        : quoteQuery.isError || quoteQuery.data == null
          ? 'failed'
          : 'ready';
  const quote = quoteStatus === 'ready' ? (quoteQuery.data ?? null) : null;

  // Whether this deposit covers the processing fee (the company's own setting always does), and the
  // server's breakdown of the request it will send.
  const companyCoversFees = quote?.companyCoversFees ?? false;
  const covered = companyCoversFees || coverFees;
  const breakdown = quote === null ? null : covered ? quote.covered : quote.uncovered;
  // Re-entering a deposit a key is already held for (a maybe-charged attempt, then a close and a
  // reopen, a remount or a reload) resends THAT request, so its own figures are the ones stated.
  const heldEntry =
    wireAmount !== null && cardId !== null
      ? series.heldFor({ amount: wireAmount, cardId, coverFees: covered })
      : null;
  const seriesRequest = heldEntry?.request ?? null;
  // The request a charge in flight or in doubt was made with: what the row and summary show then.
  const pendingRequest =
    phase.status === 'charging' || phase.status === 'retryable-error' ? phase.entry.request : null;
  const shownRequest = pendingRequest ?? seriesRequest;
  // The row's words (CAM-BE13: one helper decides every cover-fees row's wording): for the request a
  // charge was made with, or the live quote's.
  const rowQuote = shownRequest !== null ? shownRequest.quote : quote;
  const rowCopy = coverFeesRowCopy({
    figures: rowQuote === null ? null : depositQuoteFigures(rowQuote),
    rail: 'card',
  });
  const coverFeesDisabled = locked || quote === null || companyCoversFees;

  // The company deposit body (CAM-X4), resolved on each open once there is a card to deposit from.
  // A request in flight, in doubt or re-entered shows the body it was stated under; otherwise the
  // freshly resolved one. Deposit waits for a body.
  const { state: disclosureState, reload: reloadDisclosure } = useChargeDisclosure(
    open && hasCard ? 'ADD_FUNDS|company' : null,
    resolveDepositDisclosure,
  );
  const resolvedDisclosure = disclosureState.status === 'ready' ? disclosureState.value : null;
  const disclosure = shownRequest?.disclosure ?? resolvedDisclosure;

  const cardOptions: SelectOption[] = useMemo(
    () => cards.map((c) => ({ value: c.id, label: cardLabel(c) })),
    [cards],
  );
  const selectedCard = cards.find((c) => c.id === cardId) ?? null;

  // Editing the deposit after a definite no-charge (a decline, or a quote that changed) starts a new,
  // un-charged attempt, so the notice about the last one goes.
  const clearNoChargeNotice = useCallback(() => {
    setPhase((current) =>
      current.status === 'fatal-error' ||
      current.status === 'quote-changed' ||
      current.status === 'disclosure-stale' ||
      current.status === 'duplicate-recent'
        ? { status: 'idle' }
        : current,
    );
  }, []);

  // The ONLY close path. Fires `onCloseAfterAttempt` when a charge was attempted this open-session so
  // the page refreshes the wallet balance the user is told to check.
  const finalizeClose = useCallback(() => {
    if (attempted.current) onCloseAfterAttempt();
    onOpenChange(false);
  }, [onCloseAfterAttempt, onOpenChange]);

  // Dismissal gate: never close mid-flight; a maybe-charged (`retryable-error`) dismissal ALWAYS
  // routes through the explicit acknowledgement step (Esc / overlay-click / Cancel arm it, only the
  // ack button actually closes); everything else closes normally.
  const requestClose = useCallback(() => {
    if (busy) return;
    if (phase.status === 'retryable-error') {
      setCloseAck(true);
      return;
    }
    finalizeClose();
  }, [busy, phase.status, finalizeClose]);

  // Confirm-before-charge copy: states the exact charge (amount + card) WITHOUT promising the exact
  // wallet credit, from the server's quote only. Uncovered, the card is charged the amount entered and
  // the processing fee comes out of the deposit; covered (CAM-BE11), the card is charged the covered
  // gross, which the copy states with the part that covers the fee. While the quote loads or has failed
  // there is no figure to state, so the copy says why Deposit is held. The card label keeps its words
  // together (non-breaking spaces) so a phone-width wrap never parts "••••" from the last four.
  const card = selectedCard !== null ? cardLabel(selectedCard).replace(/ /g, '\u00a0') : null;
  const statedSummary =
    breakdown === null || card === null
      ? null
      : !breakdown.coverFees
        ? `This immediately charges ${formatChargeFigure(breakdown.amountCharged)} to ${card} to ` +
          `fund your company wallet.`
        : `This immediately charges ${formatFeeFigure(breakdown.amountCharged)} to ${card} to fund ` +
          `your company wallet, including ${formatFeeFigure(breakdown.processorFee)} to cover ` +
          `processing fees.`;

  // EP-BE15's question names the earlier deposit's card as the dialog labels it.
  const duplicateQuestion =
    phase.status === 'duplicate-recent'
      ? duplicateDepositQuestion({
          amount: phase.earlier.amount,
          card: (() => {
            const earlierCard = cards.find((c) => c.id === phase.earlier.cardId);
            return earlierCard === undefined
              ? null
              : cardLabel(earlierCard).replace(/ /g, '\u00a0');
          })(),
          minutesAgo: phase.earlier.minutesAgo,
        })
      : null;

  const runCharge = useCallback(async () => {
    if (inFlight.current) return; // single-flight: a second click is a no-op.

    let entry: DepositSeriesEntry;
    if (phase.status === 'retryable-error') {
      // Try again (review F2): resend EXACTLY the maybe-charged request, under its key — held again,
      // never re-minted.
      entry = series.attempt(phase.entry);
    } else if (phase.status === 'duplicate-recent') {
      // The person confirmed a deliberate second deposit (EP-BE15): the same request, confirmed, as
      // a NEW series under a fresh key — the refused one held none, it was settled when refused.
      const { body, ...held } = phase.request;
      entry = series.start({ ...held, body: { ...body, confirm_duplicate: true } });
    } else {
      if (cardId === null) return; // inert without a card — never charge.
      if (wireAmount === null) return; // never POST an unvalidated / out-of-bounds amount.
      // No quote, nothing to state.
      if (quote === null || breakdown === null || statedSummary === null) return;
      // Re-entering a deposit a key is held for (a maybe-charged attempt, then a close and a reopen,
      // a remount or a reload) resends that exact request under that key, so the backend replays it
      // rather than charging again.
      if (heldEntry !== null) {
        entry = series.attempt(heldEntry);
      } else {
        // Never deposit without the company body shown (CAM-X4) — Deposit is disarmed too.
        if (resolvedDisclosure === null) return;
        const body: CompanyDepositBody = {
          amount: wireAmount,
          payment_method: 'Credit Card',
          card_id: cardId,
          cover_fees: covered,
          expected_amount_charged: breakdown.amountCharged,
          ...disclosureEcho(resolvedDisclosure),
          // EP-BE15: ask the backend to check for a repeat; this dialog can ask the person.
          duplicate_check: 'prompt',
        };
        // Mint the stable key BEFORE the request and hold it with the request beyond this dialog, so
        // a re-entry finds it even if the answer never arrives here (a navigation, a reload).
        entry = series.start({
          amount: wireAmount,
          cardId,
          coverFees: covered,
          expectedAmountCharged: breakdown.amountCharged,
          summary: statedSummary,
          quote,
          disclosure: resolvedDisclosure,
          body,
        });
      }
    }

    inFlight.current = true;
    attempted.current = true;

    try {
      setPhase({ status: 'charging', entry });
      await sdk.companiesDepositFundsCompany({
        body: entry.request.body,
        headers: { 'Idempotency-Key': entry.key },
        throwOnError: true,
      });

      // Confirmed: DROP the series (a brand-new deposit gets a brand-new key), release the flight
      // lock, and hand success to the page (invalidate + toast + close).
      series.settle(entry);
      inFlight.current = false;
      setPhase({ status: 'idle' });
      onDeposited();
    } catch (caught: unknown) {
      inFlight.current = false;
      const err = isGivmoError(caught)
        ? caught
        : new GivmoError({ message: 'Something went wrong. Please try again.', kind: 'unknown' });
      const reference = supportReference(err);
      // RETRYABLE (maybe-charged — incl. a 409 IDEMPOTENCY_IN_PROGRESS or IDEMPOTENCY_KEY_REUSED: the
      // first request is still running or has succeeded): KEEP the series — the key AND the request —
      // so the next attempt re-POSTs the SAME charge and the backend replays it. Surface the explicit
      // check-balance/try-again state — NEVER auto-retry. Its line is the server's own sentence where
      // the server worded one, never a processor's message (EP-WEB34).
      if (isRetryableChargeError(err)) {
        setPhase({
          status: 'retryable-error',
          message: maybeChargedLine(err, RETRYABLE_HINT),
          reference,
          entry,
        });
        return;
      }
      // FATAL (definite no-charge): drop the series, so the next attempt is stated afresh under a
      // fresh key. (The consumer idempotency seam RELEASES a key on a 4xx rather than caching it —
      // only a success is stored and replayed — so this is about a stale request, not a cached
      // decline.) No optimistic success is implied.
      series.settle(entry);
      const request = entry.request;
      if (firstErrorCode(err) === 'DEPOSIT_QUOTE_CHANGED') {
        // The stated gross is no longer what the charge would take (a card-brand fee row, a re-rate,
        // the company's own setting), refused before any money moved. Discard the refused price and
        // read it again. RESET, not invalidate: an invalidated query keeps its data while it
        // refetches, so the dialog would state the refused total under "Check the new total" with
        // Deposit live. Reset empties the cache, so until the new price lands the dialog states no
        // figure and holds Deposit (fold review FR2).
        void queryClient.resetQueries({
          queryKey: depositQuoteQueryKey(request.amount, request.cardId),
        });
        setPhase({ status: 'quote-changed' });
        return;
      }
      if (isDisclosureStaleError(err)) {
        // The company body changed after it was shown, refused before any money moved: read it
        // again and ask for a fresh confirm against the new text, under a new key.
        reloadDisclosure();
        setPhase({ status: 'disclosure-stale' });
        return;
      }
      if (firstErrorCode(err) === DUPLICATE_RECENT) {
        // EP-BE15: a repeat of a deposit made minutes ago, refused before any money moved: ask.
        setPhase({ status: 'duplicate-recent', request, earlier: earlierDepositOf(err, request) });
        return;
      }
      setPhase({ status: 'fatal-error', message: err.message, reference });
    }
  }, [
    phase,
    series,
    cardId,
    wireAmount,
    quote,
    breakdown,
    statedSummary,
    heldEntry,
    resolvedDisclosure,
    covered,
    queryClient,
    onDeposited,
    reloadDisclosure,
  ]);

  // Deposit is held until the server has priced this exact amount + card (review F1) and the company
  // body is shown (CAM-X4). "Try again" resends the frozen request and so needs neither afresh.
  const canSubmit =
    phase.status === 'retryable-error'
      ? !busy
      : hasCard && validation.valid && !busy && quoteStatus === 'ready' && disclosure !== null;

  // What the summary line says: the stated sentence of the request in flight or in doubt, otherwise
  // the live quote's, or why there is none.
  const summary =
    phase.status === 'charging' || phase.status === 'retryable-error'
      ? phase.entry.request.summary
      : dollars === null || card === null
        ? null
        : quoteStatus === 'loading'
          ? QUOTE_LOADING
          : quoteStatus === 'failed'
            ? QUOTE_FAILED
            : (seriesRequest?.summary ?? statedSummary);

  const buttonLabel =
    phase.status === 'retryable-error'
      ? 'Try again'
      : dollars !== null
        ? `Deposit ${formatDollars(dollars)}`
        : 'Deposit';

  // An amount error is only shown once the field is non-empty-and-invalid or explicitly out of bounds —
  // we do not shout "enter an amount" before the user has interacted. `empty` shows only if they clear it.
  const showAmountError = !validation.valid && amount.trim().length > 0;

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        // Every dismiss vector (Esc, overlay-click, Cancel/Close) routes through the gate: an
        // in-flight charge never closes, and a maybe-charged state closes only via acknowledgement.
        if (!next) {
          requestClose();
          return;
        }
        onOpenChange(next);
      }}
    >
      <Dialog.Content
        aria-label="Add funds"
        className="flex flex-col gap-16"
        testId={`${PREFIX}-dialog`}
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Add funds to your company wallet
        </Dialog.Title>

        {!hasCard ? (
          // ── Inert (no card on file): no charge control, just the add-a-card prompt. ──
          <>
            <div
              className="flex items-start gap-10 rounded-lg border border-border-subtle bg-surface-sunken p-12"
              {...testIdProp(`${PREFIX}-no-card`)}
            >
              <Icon icon={AlertTriangle} size={18} aria-hidden className="mt-1 text-warning-600" />
              <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
                {NO_CARD_BODY}
              </p>
            </div>
            <div className="mt-4 flex items-center justify-end gap-8">
              <Dialog.Close render={<Button appearance="saas" tone="secondary" size="md" />}>
                Close
              </Dialog.Close>
            </div>
          </>
        ) : (
          <>
            <TextField
              label="Amount (USD)"
              inputMode="numeric"
              autoComplete="off"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                // Editing the amount clears a prior definite-no-charge notice (a new, un-charged
                // attempt). It is disabled during charging/retryable-error via `locked`, so it can never
                // re-scope a maybe-charged series.
                clearNoChargeNotice();
              }}
              disabled={locked}
              error={showAmountError}
              errorMessage={
                showAmountError && validation.error !== null
                  ? depositAmountErrorMessage(validation.error)
                  : undefined
              }
              testId={`${PREFIX}-amount`}
            />

            <div className="flex flex-wrap gap-8" {...testIdProp(`${PREFIX}-presets`)}>
              {DEPOSIT_PRESETS.map((preset) => (
                <Button
                  key={preset}
                  appearance="saas"
                  tone={dollars === preset ? 'primary' : 'secondary'}
                  size="sm"
                  disabled={locked}
                  onClick={() => {
                    setAmount(String(preset));
                    clearNoChargeNotice();
                  }}
                  testId={`${PREFIX}-preset-${preset}`}
                >
                  {formatDollars(preset)}
                </Button>
              ))}
            </div>

            {/* CAM-BE11: "Cover processing fees" — off by default, placed and worded like the iOS
                row (the shared row, CAM-BE13). Locked with the other fields once a charge is
                attempted; unavailable while the quote loads or has failed (unticked then); ticked and
                locked when the company's own setting covers every deposit. A locked row dims WHOLE —
                box, title and fee line (review F4) — so it never reads as live. */}
            <CoverFeesRow
              checked={
                pendingRequest !== null
                  ? pendingRequest.coverFees
                  : quoteStatus === 'failed'
                    ? false
                    : covered
              }
              onCheckedChange={(next) => {
                setCoverFees(next);
                // A new, un-charged attempt clears a prior no-charge notice. The choice is part of
                // the series signature, so the next charge keys itself; the holder is NOT cleared
                // here, so ticking back to a maybe-charged deposit's choice still replays it.
                clearNoChargeNotice();
              }}
              disabled={coverFeesDisabled}
              title={rowCopy.title}
              line={rowCopy.line}
              lineIsReason={rowCopy.lineIsReason}
              testId={`${PREFIX}-cover-fees`}
            />

            <Select
              label="Pay with"
              options={cardOptions}
              value={cardId}
              onValueChange={(v) => {
                setCardId(v);
                // Another card is another signature, so the next charge keys itself; a prior notice
                // goes. No held series is dropped (CAM-R51): choosing a maybe-charged deposit's card
                // again still replays it. Disabled while locked, so this never fires during
                // charging/retryable-error.
                clearNoChargeNotice();
              }}
              disabled={locked}
              testId={`${PREFIX}-card-select`}
            />

            {summary !== null && (
              <p
                className="font-sans text-s13 font-w500 leading-h15 tracking-givmo text-text-secondary"
                {...testIdProp(`${PREFIX}-summary`)}
              >
                {summary}
              </p>
            )}

            {phase.status === 'quote-changed' && (
              <p
                className="font-sans text-s13 font-w500 leading-h15 tracking-givmo text-danger-700"
                {...testIdProp(`${PREFIX}-quote-changed`)}
              >
                {QUOTE_CHANGED}
              </p>
            )}

            {phase.status === 'disclosure-stale' && (
              <p
                className="font-sans text-s13 font-w500 leading-h15 tracking-givmo text-danger-700"
                {...testIdProp(`${PREFIX}-disclosure-stale`)}
              >
                {DISCLOSURE_STALE}
              </p>
            )}

            {(phase.status === 'retryable-error' || phase.status === 'fatal-error') && (
              <div
                className="flex flex-col items-start gap-2"
                {...testIdProp(
                  phase.status === 'retryable-error'
                    ? `${PREFIX}-retryable-error`
                    : `${PREFIX}-fatal-error`,
                )}
              >
                <p className="font-sans text-s13 font-w500 leading-h15 tracking-givmo text-danger-700">
                  {phase.message}
                </p>
                {phase.reference !== null && (
                  <p className="font-sans text-s12 font-w400 tracking-normal text-text-tertiary">
                    {phase.reference}
                  </p>
                )}
              </div>
            )}

            {disclosure === null && disclosureState.status === 'unavailable' && (
              // ── The company body did not resolve (CAM-X4): Deposit stays disarmed; say why. ──
              <DisclosureUnavailable
                message={DISCLOSURE_UNAVAILABLE}
                onRetry={reloadDisclosure}
                testId={`${PREFIX}-disclosure-unavailable`}
              />
            )}

            {/* ── The company deposit body (CAM-X4, D-CAM-44, D-CAM-57), directly above Cancel and
                Deposit. ── */}
            {disclosure !== null && (
              <ChargeDisclosureTerms
                sections={[{ key: disclosure.contentHash, label: null, lines: disclosure.lines }]}
                testId={`${PREFIX}-disclosure`}
              />
            )}

            {duplicateQuestion !== null ? (
              // ── EP-BE15: a repeat of a deposit made minutes ago, nothing charged. The footer is
              //    replaced: add it again (a new deposit, under a fresh key) or close. ──
              <div
                className="flex flex-col gap-12 rounded-lg border border-border-subtle bg-surface-sunken p-12"
                {...testIdProp(`${PREFIX}-duplicate`)}
              >
                <p className="font-sans text-s13 font-w500 leading-h15 tracking-givmo text-text-secondary">
                  {duplicateQuestion}
                </p>
                <div className="flex items-center justify-end gap-8">
                  <Button
                    appearance="saas"
                    tone="secondary"
                    size="md"
                    onClick={finalizeClose}
                    testId={`${PREFIX}-duplicate-cancel`}
                  >
                    Cancel
                  </Button>
                  <Button
                    appearance="saas"
                    tone="primary"
                    size="md"
                    disabled={busy}
                    loading={busy}
                    onClick={() => void runCharge()}
                    testId={`${PREFIX}-duplicate-confirm`}
                  >
                    Add it again
                  </Button>
                </div>
              </div>
            ) : phase.status === 'retryable-error' && closeAck ? (
              // ── Maybe-charged close acknowledgement (invariant 2): the footer is replaced so the
              //    ONLY ways forward are an explicit acknowledged close or going back to Try again. ──
              <div
                ref={closeAckRef}
                className="flex scroll-mb-24 flex-col gap-12 rounded-lg border border-border-subtle bg-surface-sunken p-12"
                {...testIdProp(`${PREFIX}-close-ack`)}
              >
                <p className="font-sans text-s13 font-w500 leading-h15 tracking-givmo text-text-secondary">
                  {CLOSE_ACK_BODY}
                </p>
                <div className="flex items-center justify-end gap-8">
                  <Button
                    appearance="saas"
                    tone="secondary"
                    size="md"
                    onClick={() => setCloseAck(false)}
                    testId={`${PREFIX}-close-ack-back`}
                  >
                    Go back
                  </Button>
                  <Button
                    appearance="saas"
                    tone="primary"
                    size="md"
                    onClick={finalizeClose}
                    testId={`${PREFIX}-close-ack-confirm`}
                  >
                    I understand — close
                  </Button>
                </div>
              </div>
            ) : (
              <div className="mt-4 flex items-center justify-end gap-8">
                <Dialog.Close
                  disabled={busy}
                  render={<Button appearance="saas" tone="secondary" size="md" />}
                >
                  Cancel
                </Dialog.Close>
                <Button
                  appearance="saas"
                  tone="primary"
                  size="md"
                  // Single-flight: disabled while charging AND when the amount is invalid / no card,
                  // and until the quote and the company body are in. A double-click cannot fire a
                  // second POST (this + the `inFlight` ref) or mint a 2nd key.
                  disabled={!canSubmit}
                  loading={busy}
                  onClick={() => void runCharge()}
                  testId={`${PREFIX}-confirm`}
                >
                  {buttonLabel}
                </Button>
              </div>
            )}
          </>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
}
